import React from "react";
import './TermsOfService.css';

/*
 * The last-updated date is a constant, not new Date().
 *
 * Both legal pages used to render today's date, so the document claimed it had
 * been revised on whatever day you happened to read it. That defeats the
 * purpose of the field, and on something people rely on for their rights it is
 * a false statement of fact.
 *
 * This date is the last commit that changed the wording (d96defa, 20 Feb
 * 2026). It needs confirming against whatever legal counsel treats as the true
 * last revision, and updating by hand whenever the text below changes.
 */
const LAST_UPDATED = '20 February 2026';

const TermsOfService = () => {
    const sectionColors = [
        '#104378', '#0d8a72', '#8b5cf6', '#f59e0b', '#ef4444'
    ];

    return (
        <div className="terms-page">
            {/* Hero on the site baseline. .pa2 scoped to the hero — see PrivacyPolicy. */}
            <div className="pa2">
                <section className="hero" id="top">
                    <div className="wrap">
                        <h1 className="on-dark">
                            <span className="setup">The terms</span>
                            <span className="payoff">you are agreeing to.</span>
                        </h1>
                        <p className="lede">
                            Welcome to PhysioActivity. By accessing or using our website, you agree to
                            comply with and be bound by the following Terms of Service. Please read them
                            carefully.
                        </p>
                    </div>
                </section>
            </div>

            <main className="terms-main">

                {/* Content Sections */}
                <div className="terms-sections">
                    {/* Use of the Website */}
                    <div className="terms-section">
                        <div className="terms-section-header">
                            <div className="terms-section-number" style={{background: sectionColors[0]}}>1</div>
                            <h2 className="terms-section-title">Use of the Website</h2>
                        </div>
                        <div className="terms-section-content">
                            <div className="terms-check-icon" style={{background: '#e8f4f8'}}>
                                <svg fill="none" stroke="#104378" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <p className="terms-section-text">
                                You agree to use the website only for <strong>lawful purposes</strong> and in a way that
                                does not infringe the rights of, restrict, or inhibit anyone else's use of the website.
                            </p>
                        </div>
                    </div>

                    {/* Intellectual Property */}
                    <div className="terms-section">
                        <div className="terms-section-header">
                            <div className="terms-section-number" style={{background: sectionColors[1]}}>2</div>
                            <h2 className="terms-section-title">Intellectual Property</h2>
                        </div>
                        <div className="terms-section-content">
                            <div className="terms-check-icon" style={{background: '#d1f4e8'}}>
                                <svg fill="none" stroke="#0d8a72" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <p className="terms-section-text">
                                All content on this website, including <strong>text, graphics, logos, and images</strong>,
                                is the property of PhysioActivity or its content suppliers and is protected by applicable copyright and trademark laws.
                            </p>
                        </div>
                    </div>

                    {/* Disclaimer */}
                    <div className="terms-section">
                        <div className="terms-section-header">
                            <div className="terms-section-number" style={{background: sectionColors[2]}}>3</div>
                            <h2 className="terms-section-title">Disclaimer</h2>
                        </div>
                        <div className="terms-section-content">
                            <div className="terms-check-icon" style={{background: '#ede9fe'}}>
                                <svg fill="none" stroke="#8b5cf6" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <p className="terms-section-text">
                                The content on this site is for <strong>informational purposes only</strong> and should
                                not be considered medical advice. Always consult a licensed professional for medical or physical therapy-related concerns.
                            </p>
                        </div>
                    </div>

                    {/* Limitation of Liability */}
                    <div className="terms-section">
                        <div className="terms-section-header">
                            <div className="terms-section-number" style={{background: sectionColors[3]}}>4</div>
                            <h2 className="terms-section-title">Limitation of Liability</h2>
                        </div>
                        <div className="terms-section-content">
                            <div className="terms-check-icon" style={{background: '#fef3c7'}}>
                                <svg fill="none" stroke="#f59e0b" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <p className="terms-section-text">
                                We are <strong>not liable for any damages</strong> resulting from the use of, or
                                inability to use, this site or the material contained on it.
                            </p>
                        </div>
                    </div>

                    {/* Changes to Terms */}
                    <div className="terms-section">
                        <div className="terms-section-header">
                            <div className="terms-section-number" style={{background: sectionColors[4]}}>5</div>
                            <h2 className="terms-section-title">Changes to Terms</h2>
                        </div>
                        <div className="terms-section-content">
                            <div className="terms-check-icon" style={{background: '#fee2e2'}}>
                                <svg fill="none" stroke="#ef4444" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </div>
                            <p className="terms-section-text">
                                We reserve the right to <strong>change these terms at any time</strong>. Any changes will
                                be posted on this page and will take effect immediately.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Last Updated */}
                <div className="terms-last-updated">
                    <div className="terms-badge">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="terms-badge-text">
                            Last updated: {LAST_UPDATED}. Please check back for updates.
                        </span>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default TermsOfService;
